<script setup>
import { computed, onMounted, ref } from 'vue';
import { endOfDay, getUnixTime, startOfDay, subDays } from 'date-fns';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();

const filters = ref({
  inboxId: null,
  dateRange: {
    type: 'last_30_days',
    from: getUnixTime(startOfDay(subDays(new Date(), 30))),
    to: getUnixTime(endOfDay(new Date())),
  },
});

const isLoading = ref(false);
const isExporting = ref(false);
const report = ref({
  totalCount: 0,
  classifiedCount: 0,
  unclassifiedCount: 0,
  rows: [],
});

const configuredReasonsCount = computed(
  () => currentAccount.value?.settings?.contact_reason_categories?.length || 0
);

const payload = computed(() => ({
  from: filters.value.dateRange?.from,
  to: filters.value.dateRange?.to,
  inboxId: filters.value.inboxId,
}));

const hasConfiguredReasons = computed(() => configuredReasonsCount.value > 0);
const hasResults = computed(
  () => report.value.totalCount > 0 || report.value.rows.some(row => row.count > 0)
);

const withPercent = rows =>
  rows.map(row => ({
    ...row,
    percentage:
      report.value.totalCount > 0
        ? ((row.count / report.value.totalCount) * 100).toFixed(1)
        : '0.0',
  }));

const rowsWithPercent = computed(() => withPercent(report.value.rows || []));

const fetchReport = async () => {
  try {
    isLoading.value = true;
    const { data } = await ReportsAPI.getContactReasonSummary(payload.value);
    report.value = {
      totalCount: data.total_count || 0,
      classifiedCount: data.classified_count || 0,
      unclassifiedCount: data.unclassified_count || 0,
      rows: data.rows || [],
    };
  } finally {
    isLoading.value = false;
  }
};

const exportReport = async () => {
  try {
    isExporting.value = true;
    const { data } = await ReportsAPI.downloadContactReasonSummary(payload.value);
    const timestamp = filters.value.dateRange?.to || getUnixTime(new Date());
    downloadCsvFile(`contact-reason-summary-${timestamp}.csv`, data);
  } finally {
    isExporting.value = false;
  }
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  await fetchReport();
});
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('CONTACT_REASON_REPORTS.HEADER')"
      :header-description="$t('CONTACT_REASON_REPORTS.DESCRIPTION')"
    >
      <NextButton
        slate
        :label="$t('CONTACT_REASON_REPORTS.DOWNLOAD')"
        icon="i-lucide-download"
        :is-loading="isExporting"
        @click="exportReport"
      />
    </ReportHeader>

    <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
      <div class="flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <SearchDateRangeSelector
          v-model="filters.dateRange"
          class="min-w-0 max-w-full"
          @change="fetchReport"
        />
        <div class="w-px h-4 bg-n-weak flex-shrink-0 hidden lg:block" />
        <SearchInboxSelector
          v-model="filters.inboxId"
          :label="$t('CONTACT_REASON_REPORTS.INBOX_FILTER')"
          @change="fetchReport"
        />
      </div>
    </div>

    <div
      v-if="!hasConfiguredReasons"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('CONTACT_REASON_REPORTS.NO_RULES') }}
      </p>
    </div>

    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="mb-1 text-sm text-n-slate-11">{{ $t('CONTACT_REASON_REPORTS.TOTAL') }}</p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">{{ report.totalCount }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="mb-1 text-sm text-n-slate-11">{{ $t('CONTACT_REASON_REPORTS.CLASSIFIED') }}</p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">{{ report.classifiedCount }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="mb-1 text-sm text-n-slate-11">{{ $t('CONTACT_REASON_REPORTS.UNCLASSIFIED') }}</p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">{{ report.unclassifiedCount }}</p>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
      >
        <p class="m-0 text-n-slate-11">{{ $t('CONTACT_REASON_REPORTS.LOADING') }}</p>
      </div>

      <div
        v-else-if="!hasResults"
        class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
      >
        <p class="m-0 text-n-slate-11">{{ $t('CONTACT_REASON_REPORTS.EMPTY_STATE') }}</p>
      </div>

      <section v-else class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-3">{{ $t('CONTACT_REASON_REPORTS.COLUMNS.CATEGORY') }}</th>
                <th class="py-2 pr-3">{{ $t('CONTACT_REASON_REPORTS.COLUMNS.REASON') }}</th>
                <th class="py-2 pr-3">{{ $t('CONTACT_REASON_REPORTS.COLUMNS.COUNT') }}</th>
                <th class="py-2">{{ $t('CONTACT_REASON_REPORTS.COLUMNS.PERCENTAGE') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rowsWithPercent"
                :key="`${row.category_name}-${row.reason_name}`"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 pr-3 text-n-slate-12">{{ row.category_name }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.reason_name }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.count }}</td>
                <td class="py-2 text-n-slate-11">{{ row.percentage }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>
